import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Edit, Trash2, Plus, Upload, X } from 'lucide-react';
import api from '../../utils/api';

interface Banner {
  id: number;
  title: string;
  description: string | null;
  image_path: string;
  link: string | null;
  order_sequence: number | null;
  created_at?: string;
  updated_at?: string;
}

type SortOrder = 'asc' | 'desc' | null;

interface LazyParams {
  first: number;
  rows: number;
  page: number;
  sortField: string | null;
  sortOrder: SortOrder;
  filters: Record<string, any>;
}

export default function Banners() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState<LazyParams>({
    first: 0,
    rows: 15,
    page: 0,
    sortField: null,
    sortOrder: null,
    filters: {}
  });
  const { t } = useTranslation();
  const [toast, setToast] = useState({ visible: false, severity: '', summary: '', detail: '' });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchBanners();
  }, [lazyParams]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/banners', {
        params: {
          page: lazyParams.page + 1,
          per_page: lazyParams.rows,
          sort_field: lazyParams.sortField || undefined,
          sort_order: lazyParams.sortOrder,
          ...lazyParams.filters
        }
      });
      setBanners(response.data.data);
      setTotalRecords(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching banners:', error);
      showToast('error', t('error'), t('error_fetching_banners'));
      setLoading(false);
    }
  };

  const showToast = (severity: string, summary: string, detail: string) => {
    setToast({ visible: true, severity, summary, detail });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const openNew = () => {
    setEditingBanner({
      id: 0,
      title: '',
      description: '',
      image_path: '',
      link: '',
      order_sequence: null,
    });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditingBanner(null);
  };

  const saveBanner = async () => {
    if (!editingBanner) return;

    try {
      const formData = new FormData();
      formData.append('title', editingBanner.title);
      formData.append('description', editingBanner.description || '');
      formData.append('link', editingBanner.link || '');
      formData.append('order_sequence', editingBanner.order_sequence?.toString() || '');

      const imageFile = fileInputRef.current?.files?.[0];
      if (imageFile) {
        formData.append('image', imageFile);
      }

      if (editingBanner.id === 0) {
        await api.post('/banners', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', t('success'), t('banner_created'));
      } else {
        formData.append('_method', 'PUT');
        await api.post(`/banners/${editingBanner.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', t('success'), t('banner_updated'));
      }
      fetchBanners();
      hideDialog();
    } catch (error) {
      console.error('Error saving banner:', error);
      showToast('error', t('error'), t('error_saving_banner'));
    }
  };

  const editBanner = async (banner: Banner) => {
    try {
      const response = await api.get(`/banners/${banner.id}`);
      setEditingBanner(response.data);
      setDialogVisible(true);
    } catch (error) {
      console.error('Error fetching banner details:', error);
      showToast('error', t('error'), t('error_fetching_banner_details'));
    }
  };

  const confirmDeleteBanner = (banner: Banner) => {
    if (window.confirm(t('confirm_delete_banner'))) {
      deleteBanner(banner);
    }
  };

  const deleteBanner = async (banner: Banner) => {
    try {
      await api.delete(`/banners/${banner.id}`);
      showToast('success', t('success'), t('banner_deleted'));
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      showToast('error', t('error'), t('error_deleting_banner'));
    }
  };

  const onPage = (page: number) => {
    setLazyParams(prevState => ({ ...prevState, page, first: page * lazyParams.rows }));
  };

  const onSort = (field: string) => {
    const sortOrder = lazyParams.sortField === field && lazyParams.sortOrder === 'asc' ? 'desc' : 'asc';
    setLazyParams(prevState => ({ ...prevState, sortField: field, sortOrder }));
  };

  const onFilter = (field: string, value: string) => {
    setLazyParams(prevState => ({
      ...prevState,
      first: 0,
      page: 0,
      filters: { ...prevState.filters, [field]: value }
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && editingBanner) {
      setEditingBanner({ ...editingBanner, image_path: URL.createObjectURL(file) });
    }
  };

  const formatDate = (value: string) => {
    return new Date(value).toLocaleString();
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <h3 className="font-bold">{toast.summary}</h3>
          <p>{toast.detail}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('manage_banners')}</h2>
            <button
              onClick={openNew}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
            >
              <Plus className="inline-block mr-2" size={18} />
              {t('new_banner')}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('image')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('title')}
                >
                  {t('title')}
                  {lazyParams.sortField === 'title' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('description')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('link')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('order_sequence')}
                >
                  {t('order')}
                  {lazyParams.sortField === 'order_sequence' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('created_at')}
                >
                  {t('created_at')}
                  {lazyParams.sortField === 'created_at' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('updated_at')}
                >
                  {t('updated_at')}
                  {lazyParams.sortField === 'updated_at' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center">{t('loading')}</td>
                </tr>
              ) : banners.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center">{t('no_banners_found')}</td>
                </tr>
              ) : (
                banners.map((banner) => (
                  <tr key={banner.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <img 
                        src={`${process.env.REACT_APP_PIC_STORAGE}/${banner.image_path}`} 
                        alt={banner.title} 
                        className="w-20 h-20 object-cover rounded-md"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{banner.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{banner.description}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{banner.link}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{banner.order_sequence}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(banner.created_at || '')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(banner.updated_at || '')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => editBanner(banner)}
                        className="text-indigo-600 hover:text-indigo-900 mr-2 focus:outline-none focus:underline"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => confirmDeleteBanner(banner)}
                        className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center p-4 border-t border-gray-200">
          <span className="text-sm text-gray-700">
            {t('showing')} {lazyParams.first + 1} - {Math.min(lazyParams.first + lazyParams.rows, totalRecords)} {t('of')} {totalRecords}
          </span>
          <div className="flex items-center">
            <button
              onClick={() => onPage(0)}
              disabled={lazyParams.page === 0}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronsLeft size={20} />
            </button>
            <button
              onClick={() => onPage(lazyParams.page - 1)}
              disabled={lazyParams.page === 0}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="mx-4 text-sm">
              {t('page')} {lazyParams.page + 1} {t('of')} {Math.ceil(totalRecords / lazyParams.rows)}
            </span>
            <button
              onClick={() => onPage(lazyParams.page + 1)}
              disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows) - 1}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => onPage(Math.ceil(totalRecords / lazyParams.rows) - 1)}
              disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows) - 1}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {dialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingBanner && editingBanner.id ? t('edit_banner') : t('new_banner')}</h2>
              <button onClick={hideDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveBanner(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">{t('title')}</label>
                  <input
                    type="text"
                    id="title"
                    value={editingBanner?.title || ''}
                    onChange={(e) => setEditingBanner(prev => prev ? { ...prev, title: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">{t('description')}</label>
                  <textarea
                    id="description"
                    value={editingBanner?.description || ''}
                    onChange={(e) => setEditingBanner(prev => prev ? { ...prev, description: e.target.value } : null)}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="image" className="block text-sm font-medium text-gray-700">{t('image')}</label>
                  <div className="flex items-center space-x-4 mt-1">
                    {editingBanner?.image_path && (
                      <img 
                        src={editingBanner.image_path.startsWith('blob:') ? editingBanner.image_path : `${process.env.REACT_APP_PIC_STORAGE}/${editingBanner.image_path}`} 
                        alt="Banner Image" 
                        className="w-32 h-32 object-cover rounded-md" 
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Upload size={18} className="inline-block mr-2" />
                      {t('upload_image')}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="link" className="block text-sm font-medium text-gray-700">{t('link')}</label>
                  <input
                    type="text"
                    id="link"
                    value={editingBanner?.link || ''}
                    onChange={(e) => setEditingBanner(prev => prev ? { ...prev, link: e.target.value } : null)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="order_sequence" className="block text-sm font-medium text-gray-700">{t('order_sequence')}</label>
                  <input
                    type="number"
                    id="order_sequence"
                    value={editingBanner?.order_sequence || ''}
                    onChange={(e) => setEditingBanner(prev => prev ? { ...prev, order_sequence: parseInt(e.target.value) || null } : null)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

